import { Buffer } from "buffer";

import {
  CONTAINER_OID,
  DER_CONSTRUCTED_BIT,
  DER_LONG_FORM_BIT,
  DER_TAG,
  MAX_PKCS7_PAD_LENGTH,
} from "./modern-store.constants";
import { IDerNode, IPbes2Params } from "./modern-store.models";

/**
 * Minimal DER → tree. Descends into `SEQUENCE`/`SET`/any constructed tag, and
 * into an `OCTET STRING` only when its content itself starts a TLV — that is how
 * PKCS#12 nests `SafeContents`. Malformed length stops the walk rather than
 * throwing: a foreign file must fall through to the normal diagnosis, not crash.
 */
export const parseDer = (buffer: Buffer): IDerNode[] => {
  const nodes: IDerNode[] = [];
  let offset = 0;

  while (offset + 1 < buffer.length) {
    const tag = buffer[offset];
    let length = buffer[offset + 1];
    let headerLength = 2;

    if (length & DER_LONG_FORM_BIT) {
      const lengthBytes = length & 0x7f;

      if (
        lengthBytes === 0 ||
        lengthBytes > 4 ||
        offset + 2 + lengthBytes > buffer.length
      ) {
        return nodes;
      }

      length = 0;

      for (let index = 0; index < lengthBytes; index += 1) {
        length = length * 256 + buffer[offset + 2 + index];
      }

      headerLength = 2 + lengthBytes;
    }

    if (offset + headerLength + length > buffer.length) {
      return nodes;
    }

    const body = buffer.subarray(
      offset + headerLength,
      offset + headerLength + length,
    ) as Buffer;
    const node: IDerNode = { tag, body, children: [] };

    if (
      tag === DER_TAG.sequence ||
      tag === DER_TAG.set ||
      tag & DER_CONSTRUCTED_BIT
    ) {
      node.children = parseDer(body);
    } else if (
      tag === DER_TAG.octetString &&
      body.length > 2 &&
      (body[0] === DER_TAG.sequence || body[0] === DER_TAG.set)
    ) {
      node.children = parseDer(body);
    }

    nodes.push(node);
    offset += headerLength + length;
  }

  return nodes;
};

/** Dotted OID string of an OID node's body. */
export const readOid = (node: IDerNode): string => {
  const bytes = node.body;
  const parts = [Math.floor(bytes[0] / 40), bytes[0] % 40];
  let value = 0;

  for (let index = 1; index < bytes.length; index += 1) {
    value = value * 128 + (bytes[index] & 0x7f);

    if (!(bytes[index] & 0x80)) {
      parts.push(value);
      value = 0;
    }
  }

  return parts.join(".");
};

/** The known name for an OID node, or `""` when the OID is not one we handle. */
export const oidName = (node: IDerNode): string =>
  CONTAINER_OID[readOid(node)] ?? "";

/** Small unsigned integer from an INTEGER node — iteration counts, nothing wider. */
export const readUnsignedInt = (node: IDerNode): number =>
  node.body.length <= 6 ? node.body.readUIntBE(0, node.body.length) : 0;

/** Depth-first visit of every node in the tree. */
export const walkDerNodes = (
  nodes: IDerNode[],
  visit: (node: IDerNode) => void,
): void => {
  nodes.forEach(node => {
    visit(node);

    if (node.children.length) {
      walkDerNodes(node.children, visit);
    }
  });
};

/** Калина runs in CBC, so a decrypted key still carries a PKCS#7 pad. */
export const stripPkcs7Padding = (buffer: Buffer): Buffer => {
  const padLength = buffer[buffer.length - 1];

  if (
    padLength > 0 &&
    padLength <= MAX_PKCS7_PAD_LENGTH &&
    buffer.length >= padLength
  ) {
    const pad = buffer.subarray(buffer.length - padLength);

    if (pad.every(byte => byte === padLength)) {
      return buffer.subarray(0, buffer.length - padLength) as Buffer;
    }
  }

  return buffer;
};

/** Lifts the PBES2 parameters out of one `EncryptedPrivateKeyInfo`, or `null`. */
export const readPbes2Params = (
  encryptedInfo: IDerNode,
): IPbes2Params | null => {
  const [algId, cipherText] = encryptedInfo.children;

  if (!algId || !cipherText || cipherText.tag !== DER_TAG.octetString) {
    return null;
  }

  const [algOid, algParams] = algId.children;

  if (!algOid || oidName(algOid) !== "PBES2" || !algParams) {
    return null;
  }

  const [kdfSeq, encSeq] = algParams.children;

  if (!kdfSeq || !encSeq) {
    return null;
  }

  const [kdfOid, kdfParams] = kdfSeq.children;

  if (!kdfOid || oidName(kdfOid) !== "PBKDF2" || !kdfParams) {
    return null;
  }

  const salt = kdfParams.children.find(
    child => child.tag === DER_TAG.octetString,
  );
  const iters = kdfParams.children.find(child => child.tag === DER_TAG.integer);
  const prfSeq = kdfParams.children.find(
    child => child.tag === DER_TAG.sequence,
  );
  const prfOid = prfSeq?.children.find(child => child.tag === DER_TAG.oid);

  const [encOid, encParams] = encSeq.children;
  const iv = encParams?.children.find(
    child => child.tag === DER_TAG.octetString,
  );

  if (!salt || !iters || !prfOid || !encOid || !iv) {
    return null;
  }

  return {
    format: "PBES2",
    kdf: oidName(prfOid),
    enc: oidName(encOid),
    salt: Buffer.from(salt.body),
    iters: readUnsignedInt(iters),
    iv: Buffer.from(iv.body),
    body: Buffer.from(cipherText.body),
  };
};
